/**
 * The selection bar's actions (`equipment-page-bulk-actions`, R1; frame 07,
 * `Equipment Page.dc.html:410–416`): *Rename as a Set*, *Change Product*, *Set 3D Model* and
 * *Keep Off Plan*, rendered into `EquipmentSelectionBar`'s `actions` slot. A selection holding
 * kept-off rows also offers *Put Back On Plan* in *Keep Off Plan*'s slot — instead of it when
 * nothing can be kept off, beside it with each one's count when both apply
 * (`equipment-page-keep-off-plan-undo`, R2; `Equipment take Keep off plan off.html`, frame 3).
 *
 * One write at a time (R9): while any of them — or the identify call behind *Change Product* — is
 * saving, every action is `aria-disabled` (still focusable) and the running one is busy; the bar's
 * *Remove* follows through `onBusyChange`. An action that cannot apply to the selection stays
 * visible and focusable, `aria-disabled`, and says why in its name — *Remove*'s pattern.
 *
 * Rows arrive in the order shown (`equipmentShownOrder`), never the tick order: *Rename as a Set*
 * numbers in it and *Change Product* seeds its picker from the first.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Icon, type IconName } from '@repo/design-system';
import { useIdentifyGroup, type IdentifyGroupApi } from '@repo/ui';
import {
  classifyKeepOffPlan,
  type AssetPlanState,
  type SetRenamePlan,
} from '@repo/front-end-utils';
import type { TwinAssetResponse } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import { useRegisterIdentifyBinding } from './EquipmentIdentify';
import { EquipmentChangeProductPopover } from './EquipmentChangeProductPopover';
import { EquipmentRenameSetPopover } from './EquipmentRenameSetPopover';
import { EquipmentSetModelPopover } from './EquipmentSetModelPopover';
import {
  ANOTHER_CHANGE_SAVING,
  identifyEnded,
  identifyRunning,
  identifyStarted,
  useEquipmentBulkWrites,
  useIdentifyRunning,
} from './useEquipmentBulkWrites';
import type { EquipmentWriteDeps } from './useEquipmentRowWrites';

export const KEEP_OFF_PLAN_UNREAD = 'Keep Off Plan — unavailable until the plan has loaded';
export const KEEP_OFF_PLAN_NONE = 'Keep Off Plan — none of these can be kept off the plan';
const SAVING = 'unavailable while a change is saving';

/** The identify binding's inputs — the ones `EquipmentAssetCardHost` is given. */
export interface BulkIdentifyInputs {
  twinId: number | null;
  baseUrl: string;
  getToken: () => Promise<string>;
  currentTwinId: () => number | null;
  reloadRegister: () => void;
  beginRegisterWrite: () => number | null;
  settleRegisterWrite: (
    token: number | null,
    outcome: { ok: boolean; reason?: string | null },
  ) => void;
}

export interface EquipmentBulkActionsProps {
  /** The selected rows, in the order shown. */
  rows: readonly TwinAssetResponse[];
  registerRows: readonly TwinAssetResponse[];
  /** The register-joined layout, for the twin's names. */
  layout: FloorPlannerLayout | null;
  /** Whether the plan is read (`identifyModel.planKnown`). */
  planKnown: boolean;
  planStateOf: (id: number) => AssetPlanState | undefined;
  /**
   * The plan state as it is NOW, read by a put back's Undo: the toast outlives this component (the
   * step can change and a row be placed meanwhile), so the page's getter, never this render's.
   */
  livePlanStateOf?: (id: number) => AssetPlanState | undefined;
  writes: EquipmentWriteDeps;
  identify: BulkIdentifyInputs;
  loadModelFiles: (options?: { refresh?: boolean }) => Promise<string[]>;
  organisationName?: string;
  onBusyChange?: (busy: boolean) => void;
  /** The page's clear of a plan model, for a productless row whose model was cleared. */
  onModelCleared?: (rowId: number) => void;
}

type Open = 'rename' | 'product' | 'model' | null;

export const EquipmentBulkActions: React.FC<EquipmentBulkActionsProps> = ({
  rows,
  registerRows,
  layout,
  planKnown,
  planStateOf,
  livePlanStateOf: pagePlanStateOf,
  writes,
  identify: identifyInputs,
  loadModelFiles,
  organisationName,
  onBusyChange,
  onModelCleared,
}) => {
  // One write at a time from the bar (R9): Change Product's identify call and its Undo are counted
  // here, since the identify hook flags only the call as busy, and the bulk runs refuse to start
  // while any is in flight; they in turn hold back while a bulk run saves.
  // Counted in the bar's shared per-twin lock, so a bar mounted later still sees them.
  const twinRef = useRef(writes.twinId);
  twinRef.current = writes.twinId;
  const identifyWriting = useIdentifyRunning(writes.twinId);
  const bulk = useEquipmentBulkWrites(
    writes,
    () => identifyRunning(twinRef.current),
    onModelCleared,
  );
  // The binding's map of groups a write has just landed on: nothing here reads it, but the
  // binding requires one (as `EquipmentAssetCardHost`).
  const settling = useRef(new Map<string, readonly number[]>());
  const { api: boundApi, fetchers } = useRegisterIdentifyBinding({ ...identifyInputs, settling });
  const { isBusy: bulkIsBusy } = bulk;
  const api = useMemo<IdentifyGroupApi>(() => {
    const counted =
      <A extends unknown[], R>(call: (...args: A) => Promise<R>) =>
      async (...args: A): Promise<R> => {
        const twin = twinRef.current;
        identifyStarted(twin);
        try {
          return await call(...args);
        } finally {
          identifyEnded(twin);
        }
      };
    return {
      ...boundApi,
      identify: counted(boundApi.identify),
      undo: counted(boundApi.undo),
      // The identify hook's Undo checks only this, and its call does not wait on an Undo: a toast's
      // Undo or Try again is held back while any Change Product call or Undo saves, too.
      writeBlocked: () =>
        bulkIsBusy() || identifyRunning(twinRef.current) ? ANOTHER_CHANGE_SAVING : null,
    };
  }, [boundApi, bulkIsBusy]);
  const { busy: identifyBusy, identify } = useIdentifyGroup(api);

  // `settling`: a run's re-read has not landed, so an action now would read the rows as they were.
  const busy = bulk.busy !== null || bulk.settling || identifyBusy || identifyWriting;
  useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);

  const [open, setOpen] = useState<Open>(null);
  const renameRef = useRef<HTMLButtonElement>(null);
  const productRef = useRef<HTMLButtonElement>(null);
  const modelRef = useRef<HTMLButtonElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  // The plan as it is now, for a put back's Undo: its toast outlives the render that made it, and
  // a row placed since must be read as placed (R7).
  const planStateRef = useRef(planStateOf);
  planStateRef.current = planStateOf;
  const ownPlanStateOf = useCallback((id: number) => planStateRef.current(id), []);
  const livePlanStateOf = pagePlanStateOf ?? ownPlanStateOf;
  const renameId = useId();
  const productId = useId();
  const modelId = useId();

  const n = rows.length;
  const shownIds = useMemo(() => rows.map(row => row.id), [rows]);
  const keepBuckets = useMemo(
    () => (planKnown ? classifyKeepOffPlan(rows, planStateOf) : null),
    [rows, planKnown, planStateOf],
  );
  const keepable = keepBuckets?.eligible.length ?? 0;
  const keptOff = keepBuckets?.keptOff.length ?? 0;
  const keepUnavailable = !planKnown
    ? KEEP_OFF_PLAN_UNREAD
    : keepable === 0
      ? KEEP_OFF_PLAN_NONE
      : null;

  // A selection that empties closes whatever is open.
  useEffect(() => {
    if (n === 0) setOpen(null);
  }, [n]);

  const toggle = (which: Exclude<Open, null>) => {
    if (busy) return;
    setOpen(current => (current === which ? null : which));
  };
  const close = () => setOpen(null);

  const action = (
    which: Exclude<Open, null> | 'keep' | 'back',
    ref: React.RefObject<HTMLButtonElement | null>,
    label: string,
    icon: IconName,
    variant: 'primary' | 'frosted' | 'text',
    unavailable: string | null,
    onPress: () => void,
    popoverId?: string,
  ) => {
    const reason = busy ? `${label} — ${SAVING}` : unavailable;
    const running =
      (which === 'rename' && bulk.busy === 'rename') ||
      (which === 'model' && bulk.busy === 'model') ||
      // One lock kind covers both directions of the mark.
      ((which === 'keep' || which === 'back') && bulk.busy === 'keep') ||
      (which === 'product' && (identifyBusy || identifyWriting));
    return (
      <Button
        ref={ref}
        variant={variant}
        size="sm"
        iconLeft={<Icon name={icon} size="sm" />}
        loading={running}
        aria-disabled={reason ? true : undefined}
        aria-label={reason ?? undefined}
        {...(popoverId
          ? {
              'aria-haspopup': 'dialog' as const,
              'aria-expanded': open === which,
              'aria-controls': popoverId,
            }
          : {})}
        onClick={() => {
          if (reason) return;
          onPress();
        }}
      >
        {label}
      </Button>
    );
  };

  // A mark run whose re-read removes the second slot (a mixed selection's *Put Back On Plan · {P}*)
  // drops focus to the page: when the counts change after a mark press, focus returns to whichever
  // mark button remains.
  const markPressed = useRef(false);
  useEffect(() => {
    if (!markPressed.current) return;
    markPressed.current = false;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    (keepRef.current ?? backRef.current)?.focus();
  }, [keepable, keptOff]);
  const keep = () => {
    close();
    markPressed.current = true;
    void bulk.keepOffPlan(rows, planStateOf);
  };
  const putBack = () => {
    close();
    markPressed.current = true;
    void bulk.putBackOnPlan(rows, livePlanStateOf);
  };

  const rename = async (named: { id: number; name: string }[], plan: SetRenamePlan) => {
    const outcome = await bulk.renameSet(named, plan);
    if (outcome !== 'busy' && outcome !== 'refused') close();
  };

  const changeProduct = async (referenceId: string, ids: readonly number[]) => {
    const outcome = await identify(
      { label: `${ids.length} selected` },
      referenceId,
      ids,
      failed => failed,
      undefined,
      undefined,
      {
        verb: 'changed',
      },
    );
    if (outcome === 'moved' || outcome === 'stayed') close();
  };

  const setModel = async (choice: string | null) => {
    await bulk.setModel(rows, choice);
    close();
  };

  return (
    <>
      {action(
        'rename',
        renameRef,
        'Rename as a Set',
        'i-cursor',
        'primary',
        null,
        () => toggle('rename'),
        renameId,
      )}
      {action(
        'product',
        productRef,
        'Change Product',
        'industry',
        'frosted',
        null,
        () => toggle('product'),
        productId,
      )}
      {action(
        'model',
        modelRef,
        'Set 3D Model',
        'cube',
        'frosted',
        null,
        () => toggle('model'),
        modelId,
      )}
      {/* The first mark slot is always one Button, relabelled in place, so a run that turns the
          selection from keepable to kept off (or back) keeps focus on the pressed button. */}
      {keptOff > 0 && keepable === 0
        ? action('back', backRef, 'Put Back On Plan', 'eye', 'primary', null, putBack)
        : action(
            'keep',
            keepRef,
            keptOff > 0 ? `Keep Off Plan · ${keepable}` : 'Keep Off Plan',
            'eye-slash',
            'text',
            keepUnavailable,
            keep,
          )}
      {keptOff > 0 &&
        keepable > 0 &&
        action('back', backRef, `Put Back On Plan · ${keptOff}`, 'eye', 'primary', null, putBack)}

      <EquipmentRenameSetPopover
        id={renameId}
        open={open === 'rename'}
        onClose={close}
        anchorRef={renameRef}
        rows={rows}
        layout={layout}
        registerRows={registerRows}
        busy={bulk.busy === 'rename'}
        onRename={(named, plan) => void rename(named, plan)}
      />
      <EquipmentChangeProductPopover
        id={productId}
        open={open === 'product'}
        onClose={close}
        anchorRef={productRef}
        shownIds={shownIds}
        fetchers={fetchers}
        organisationName={organisationName}
        logContext={identifyInputs.twinId !== null ? { twinId: identifyInputs.twinId } : undefined}
        busy={identifyBusy}
        onChange={(referenceId, ids) => void changeProduct(referenceId, ids)}
      />
      <EquipmentSetModelPopover
        id={modelId}
        open={open === 'model'}
        onClose={close}
        anchorRef={modelRef}
        count={n}
        loadModelFiles={loadModelFiles}
        busy={bulk.busy === 'model'}
        onSet={choice => void setModel(choice)}
      />
    </>
  );
};

export default EquipmentBulkActions;
